/**
 * DataAtRest.jsx
 * ---------------------------------------------------------------------------
 * Section 5. Flagship enterprise offering.
 *
 * Three blocks:
 *   1. Problem narrative + interactive realization-gap funnel
 *   2. What Data At Rest™ does (numbered list)
 *   3. Ethical positioning card + security architecture card
 *
 * Closes with the Request a Pilot Analysis CTA.
 */

import React, { useState } from "react";
import Section from "../ui/Section.jsx";
import { H2, Lede } from "../ui/Heading.jsx";
import Button from "../ui/Button.jsx";
import { tokens } from "../../lib/tokens.js";

const FUNNEL_STAGES = [
  {
    label: "Work Performed",
    pct: 100,
    note: "The full scope of effort across a matter.",
  },
  {
    label: "Time Recorded",
    pct: 78,
    note: "Between 60–90% captured in practice.",
  },
  {
    label: "Time Billed",
    pct: 58,
    note: "After internal reductions and write-downs.",
  },
  { label: "Fees Collected", pct: 42, note: "Final economic realization." },
];

const CAPABILITIES = [
  "Reconstruct workstreams across communications, drafting, research, and coordination.",
  "Normalize fragmented data into coherent narratives.",
  "Identify contribution patterns across attorneys.",
  "Surface unbilled or underrepresented value.",
  "Deliver analytical reports and visualizations.",
];

const ETHICAL_POINTS = [
  "No legal advice provided",
  "No interaction with firm clients",
  "No influence on billing decisions",
  "Firms retain full control",
];

const SECURITY_SPEC = [
  ["Encryption at rest", "AES-256"],
  ["Encryption in transit", "TLS 1.3"],
  ["Access control", "Role-based"],
  ["Data destruction", "15 days"],
  ["Compliance", "SOC 2 aligned"],
];

export default function DataAtRest() {
  return (
    <Section id="data-at-rest" label="IV · Data At Rest™">
      {/* Problem narrative + funnel */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.1fr",
          gap: 96,
          alignItems: "start",
        }}
      >
        <div style={{ position: "sticky", top: 120 }}>
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 13,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: tokens.accent,
              marginBottom: 24,
              fontStyle: "italic",
            }}
          >
            Flagship Enterprise Offering
          </div>
          <H2>Reveal the economic reality of legal work.</H2>
          <Lede style={{ marginTop: 32 }}>
            <strong style={{ color: tokens.ink, fontWeight: 500 }}>
              Legal work generates more value than it captures.
            </strong>{" "}
            Every matter includes extensive coordination, drafting, research,
            and strategic thinking that is never fully reflected in billing
            narratives.
          </Lede>
          <Lede>
            Emails, drafts, research, and internal collaboration materially
            advance matters but are compressed into minimal billing entries or
            omitted entirely. The result is a structural gap between effort and
            economic recognition.
          </Lede>
        </div>

        <FunnelDiagram />
      </div>

      {/* What Data At Rest does */}
      <div
        style={{
          marginTop: 120,
          display: "grid",
          gridTemplateColumns: "1fr 1.2fr",
          gap: 80,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: tokens.inkSoft,
              marginBottom: 24,
            }}
          >
            What Data At Rest™ Does
          </div>
          <H2 style={{ fontSize: "clamp(28px, 3.2vw, 38px)" }}>
            An analytical layer for the work your firm already performs.
          </H2>
        </div>
        <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {CAPABILITIES.map((t, i) => (
            <li
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "48px 1fr",
                gap: 20,
                padding: "22px 0",
                borderTop: `1px solid ${tokens.divider}`,
                borderBottom:
                  i === CAPABILITIES.length - 1
                    ? `1px solid ${tokens.divider}`
                    : "none",
              }}
            >
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 16,
                  color: tokens.accent,
                  fontStyle: "italic",
                  letterSpacing: "0.08em",
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 16,
                  color: tokens.ink,
                  lineHeight: 1.55,
                }}
              >
                {t}
              </span>
            </li>
          ))}
        </ol>
      </div>

      {/* Ethical + Security blocks */}
      <div
        style={{
          marginTop: 96,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 32,
        }}
      >
        <EthicalCard />
        <SecurityCard />
      </div>

      <div style={{ marginTop: 72, textAlign: "center" }}>
        <Button
          onClick={() =>
            document
              .getElementById("firms")
              ?.scrollIntoView({ behavior: "smooth" })
          }
        >
          Request a Pilot Analysis
        </Button>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function FunnelDiagram() {
  const [active, setActive] = useState(null);

  return (
    <div>
      <div
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: tokens.inkSoft,
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>The Realization Gap</span>
        <span>Illustrative</span>
      </div>

      <div
        style={{
          border: `1px solid ${tokens.divider}`,
          background: tokens.paper,
          padding: "48px 40px",
        }}
      >
        {FUNNEL_STAGES.map((s, i) => {
          const isActive = active === i;
          return (
            <div
              key={s.label}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              style={{
                marginBottom: i === FUNNEL_STAGES.length - 1 ? 0 : 24,
                cursor: "default",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 10,
                }}
              >
                <span
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 20,
                    color: tokens.ink,
                    fontWeight: 500,
                  }}
                >
                  {s.label}
                </span>
                <span
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13,
                    color: isActive ? tokens.accent : tokens.inkSoft,
                    letterSpacing: "0.04em",
                    transition: "color 180ms",
                  }}
                >
                  {s.pct}%
                </span>
              </div>
              <div
                style={{
                  position: "relative",
                  height: 32,
                  background: "transparent",
                  border: `1px solid ${tokens.divider}`,
                }}
              >
                <div
                  style={{
                    width: `${s.pct}%`,
                    height: "100%",
                    background:
                      i === 0
                        ? tokens.ink
                        : `linear-gradient(90deg, ${tokens.ink} 0%, ${tokens.accent} 100%)`,
                    opacity: 0.92 - i * 0.12,
                    transition: "opacity 200ms",
                  }}
                />
                {[0.25, 0.5, 0.75].map((t) => (
                  <div
                    key={t}
                    style={{
                      position: "absolute",
                      left: `${t * 100}%`,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      background: tokens.divider,
                    }}
                  />
                ))}
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 12,
                  color: tokens.inkSoft,
                  marginTop: 8,
                  opacity: isActive ? 1 : 0.7,
                  transition: "opacity 180ms",
                }}
              >
                {s.note}
              </div>
            </div>
          );
        })}

        <div
          style={{
            marginTop: 36,
            paddingTop: 24,
            borderTop: `1px solid ${tokens.divider}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 18,
              color: tokens.ink,
              fontStyle: "italic",
            }}
          >
            Realization gap
          </div>
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 32,
              color: tokens.accent,
              fontWeight: 500,
              letterSpacing: "-0.01em",
            }}
          >
            —58%
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function EthicalCard() {
  return (
    <div
      style={{
        background: tokens.paper,
        padding: "40px 44px",
        border: `1px solid ${tokens.divider}`,
      }}
    >
      <div
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: tokens.inkSoft,
          marginBottom: 24,
        }}
      >
        Ethical Positioning
      </div>
      {ETHICAL_POINTS.map((t) => (
        <div
          key={t}
          style={{
            display: "flex",
            gap: 14,
            padding: "14px 0",
            borderTop: `1px solid ${tokens.divider}`,
            fontFamily: "'Inter', sans-serif",
            fontSize: 15,
            color: tokens.ink,
          }}
        >
          <span
            style={{
              color: tokens.accent,
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 18,
              fontStyle: "italic",
            }}
          >
            §
          </span>
          {t}
        </div>
      ))}
    </div>
  );
}

function SecurityCard() {
  return (
    <div style={{ background: tokens.ink, padding: "40px 44px", color: "#fff" }}>
      <div
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.55)",
          marginBottom: 24,
        }}
      >
        Security Architecture
      </div>
      {SECURITY_SPEC.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "14px 0",
            borderTop: "1px solid rgba(255,255,255,0.12)",
            fontFamily: "'Inter', sans-serif",
            fontSize: 14,
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.7)" }}>{k}</span>
          <span
            style={{
              color: "#fff",
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 17,
            }}
          >
            {v}
          </span>
        </div>
      ))}
    </div>
  );
}
